import { useState } from 'react';

import { Badge, Modal } from '@thefarmersfront/kitchen';

import {
  FormField,
  ImageViewModal,
  ModalContentsContainer,
  ModalLabelValueRow,
  ModalSection,
  Table,
  ZoomableImagePreview,
} from '~/components/common';

import { useModal } from '~/hooks/common';
import { formatDate, formatReceiveExpectedDateTime, getReceiveReportDetailTasksColumnDefs } from '~/utils';
import { formatNumber } from '~/utils/common';

import { DATE_FORMAT, RECEIVE_REPORT_STATUS, RECEIVE_REPORT_STATUS_BADGE_COLOR } from '~/constants';

import type { TypeReceiveReportHistoryDetail } from '~/types/receive.types';

import styles from './ReceiveReportDetailModal.module.scss';

const ReceiveReportDetailModal = ({
  open,
  data,
  size = 'xlarge',
  onClose,
}: {
  open: boolean;
  data: TypeReceiveReportHistoryDetail;
  size?: 'xlarge' | 'large';
  onClose: () => void;
}) => {
  const [taskIssueImages, setTaskIssueImages] = useState<TypeReceiveReportHistoryDetail['tasks'][number]['images']>([]);
  const [completedReportImage, setCompletedReportImage] = useState<
    TypeReceiveReportHistoryDetail['images'][number] | null
  >(null);

  const issueImagesModal = useModal({
    onClose: () => setTaskIssueImages([]),
  });

  const completedReportImageModal = useModal({
    onClose: () => setCompletedReportImage(null),
  });

  const { columnDefs: taskColumnDefs } = getReceiveReportDetailTasksColumnDefs({
    onClickViewIssueImages: (data: TypeReceiveReportHistoryDetail['tasks'][number]) => {
      setTaskIssueImages(data.images);
      issueImagesModal.handleOpenModal();
    },
  });

  return (
    <Modal title="입고 리포트 상세" size={size} open={open} onConfirm={onClose}>
      <ModalContentsContainer>
        <ModalSection title="리포트 내역">
          <FormField
            label="리포트 상태"
            contentAlign="center"
            contents={
              <Badge variant="light" status color={RECEIVE_REPORT_STATUS_BADGE_COLOR[data.report.reportStatus]}>
                {RECEIVE_REPORT_STATUS[data.report.reportStatus].text}
              </Badge>
            }
            contentsClassName={styles['badge-value']}
          />
          <ModalLabelValueRow label="리포트 ID" value={data.report.reportCode} />
          <ModalLabelValueRow label="리포트 SKU" value={formatNumber(data.report.sku)} />
          <ModalLabelValueRow label="리포트 Unit" value={formatNumber(data.report.unit)} />
          <ModalLabelValueRow
            label="리포트 발행 일시"
            value={formatDate(data.report.createdAt, DATE_FORMAT.DATE_TIME) ?? '-'}
          />
          <ModalLabelValueRow
            label="리포트 상태 변경일시"
            value={formatDate(data.report.statusUpdatedAt, DATE_FORMAT.DATE_TIME) ?? '-'}
          />
          <ModalLabelValueRow label="공급사" value={data.report.supplierName} />
          <ModalLabelValueRow label="입고요청번호" value={data.report.requestCode} />
          <ModalLabelValueRow label="입고요청 SKU" value={formatNumber(data.report.requestSku)} />
          <ModalLabelValueRow label="입고요청 Unit" value={formatNumber(data.report.requestUnit)} />
          <ModalLabelValueRow
            label="입고예정일시"
            value={formatReceiveExpectedDateTime(data.report.receivingExpectedDate, data.report.receivingExpectedTime)}
          />
          <ModalLabelValueRow
            label="입고확정일시"
            value={formatDate(data.report.receivedDateTime, DATE_FORMAT.DATE_TIME) ?? '-'}
          />
        </ModalSection>
        <ModalSection title="리포트 발행 항목">
          <Table className={styles['table']} data={data.tasks} options={{ columnDefs: taskColumnDefs }} />
        </ModalSection>
        <ModalSection title="입고 처리 상세">
          {data.report.comment && (
            <ModalLabelValueRow
              className={styles['comment-row']}
              label="리포트 발행 코멘트"
              value={data.report.comment}
              alignItems="start"
            />
          )}
          {data.report.completedComment && (
            <div className={styles['completed-report-container']}>
              <ModalLabelValueRow
                className={styles['comment-row']}
                label="리포트 처리 완료 코멘트"
                value={data.report.completedComment}
                alignItems="start"
              />
              {data.images.length > 0 && (
                <div className={styles['report-images-wrapper']}>
                  {data.images.map((image) => (
                    <ZoomableImagePreview
                      key={image.filePath}
                      imageUrl={image.filePath}
                      alt="리포트 처리 완료 이미지"
                      onClickZoom={() => {
                        setCompletedReportImage(image);
                        completedReportImageModal.handleOpenModal();
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          {data.report.closedComment && (
            <ModalLabelValueRow
              className={styles['comment-row']}
              label="리포트 발행 취소 코멘트"
              value={data.report.closedComment}
              alignItems="start"
            />
          )}
        </ModalSection>
      </ModalContentsContainer>
      {issueImagesModal.isOpen && taskIssueImages.length > 0 && (
        <ImageViewModal
          title="이슈 이미지"
          isOpen={issueImagesModal.isOpen}
          onClose={issueImagesModal.handleCloseModal}
          imageList={taskIssueImages}
          extractor={{
            getId: (item) => item.filePath,
            getUrl: (item) => item.filePath,
          }}
        />
      )}
      {completedReportImageModal.isOpen && completedReportImage && (
        <ImageViewModal
          title="리포트 처리 완료 이미지"
          size="small"
          isOpen={completedReportImageModal.isOpen}
          onClose={completedReportImageModal.handleCloseModal}
          imageList={[completedReportImage]}
          extractor={{
            getId: (item) => item.filePath,
            getUrl: (item) => item.filePath,
          }}
        />
      )}
    </Modal>
  );
};

export default ReceiveReportDetailModal;
